import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Settings, Plane, CreditCard, Lock, Database, X, Users, Info, Search, MapPin, Plus, FileText } from 'lucide-react';
import { useAuth, TIER_LIMITS } from '../../contexts/AuthContext';
import { setAnalyticsConsent } from '../../lib/analytics';
import { features, tierConfig } from '../../lib/features';
import { getSession } from '../../lib/supabase';
import PrivacySettings from '../community/PrivacySettings';
import BlockedUsersList from '../community/BlockedUsersList';
import { FONTS, BTN } from '../../lib/theme';
import { checkDestinationRestriction } from '../../lib/travelSafety';
import { COMPANION_OPTIONS as CANONICAL_COMPANION_OPTIONS } from '@wander/shared/utils/onboardingSchema.js';

const ProfileEditor = React.lazy(() => import('../community/ProfileEditor'));
const SkeletonTripBuilder = React.lazy(() => import('../community/SkeletonTripBuilder'));
const TripImporter = React.lazy(() => import('../community/TripImporter'));

const SettingsModal = ({ isOpen, onClose }) => {
  const {
    profile,
    usage,
    updateProfile,
    updateAnalyticsConsent,
    exportData,
    signOut,
    getTierLimits,
    isPaymentPastDue,
  } = useAuth();

  const [activeTab, setActiveTab] = useState('preferences');
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [exportReason, setExportReason] = useState('');
  const [exportReasonText, setExportReasonText] = useState('');
  const [showSkeletonBuilder, setShowSkeletonBuilder] = useState(false);
  const [showTripImporter, setShowTripImporter] = useState(false);

  // Derive usage limits from tier config (source of truth), not usage_tracking table
  const _tierLimits = TIER_LIMITS[profile?.tier] || TIER_LIMITS.explorer;
  const itineraryLimit = _tierLimits.itinerariesPerQuarter;
  const recLimit = _tierLimits.destinationRecommendations;

  // Community privacy settings state
  const [communitySettings, setCommunitySettings] = useState({
    profile_visibility: profile?.privacy_settings?.profile_visibility || 'public',
    trip_default_visibility: profile?.privacy_settings?.trip_default_visibility || 'public',
    show_in_leaderboards: profile?.privacy_settings?.show_in_leaderboards ?? true,
  });

  // Travel preferences state
  const parseJsonArray = (field) => {
    if (!field) return [];
    if (Array.isArray(field)) return field;
    try {
      const parsed = JSON.parse(field);
      if (Array.isArray(parsed)) return parsed;
    } catch {}
    return [];
  };

  // Hydrate from canonical preferences JSONB (legacy columns dropped in
  // unification migration). The state uses snake_case keys for legacy
  // compatibility with the existing form code; updateUserProfile auto-
  // normalizes them back to canonical on save.
  const _prefs = profile?.preferences || {};
  const [travelPrefs, setTravelPrefs] = useState({
    home_city: _prefs.homeCity || '',
    interests: Array.isArray(_prefs.interests) ? _prefs.interests : parseJsonArray(_prefs.interests),
    dietary_restrictions: Array.isArray(_prefs.dietary) ? _prefs.dietary : parseJsonArray(_prefs.dietary),
    liked_cuisines: Array.isArray(_prefs.likedCuisines) ? _prefs.likedCuisines : parseJsonArray(_prefs.likedCuisines),
    disliked_cuisines: Array.isArray(_prefs.dislikedCuisines) ? _prefs.dislikedCuisines : parseJsonArray(_prefs.dislikedCuisines),
    accessibility_needs: Array.isArray(_prefs.accessibilityNeeds) ? _prefs.accessibilityNeeds : parseJsonArray(_prefs.accessibilityNeeds),
    safety_considerations: parseJsonArray(_prefs.safetyConsiderations),
    travel_pace: _prefs.pace || '',
    default_budget: _prefs.budget || '',
    // companions is now a multi-select array; coerce stored string→array for legacy data
    travel_companions: Array.isArray(_prefs.companions)
      ? _prefs.companions
      : (_prefs.companions ? [_prefs.companions] : []),
    safety_notes: _prefs.safetyConsiderations || '',
    travel_notes: _prefs.travelNotes || '',
    favorite_destinations: parseJsonArray(_prefs.likedPlaces),
    least_favorite_destinations: parseJsonArray(_prefs.avoidPlaces),
    dream_destinations: parseJsonArray(_prefs.likedPlaces),
    loved_places: parseJsonArray(_prefs.likedPlaces),
    avoid_places: parseJsonArray(_prefs.avoidPlaces),
    passport_countries: typeof _prefs.passportCountry === 'string' && _prefs.passportCountry
      ? parseJsonArray(_prefs.passportCountry).length > 0
        ? parseJsonArray(_prefs.passportCountry)
        : [_prefs.passportCountry]
      : (Array.isArray(_prefs.passportCountry) ? _prefs.passportCountry : []),
  });
  const [newCuisineInput, setNewCuisineInput] = useState('');
  const [newDislikedCuisineInput, setNewDislikedCuisineInput] = useState('');
  const [newPassportInput, setNewPassportInput] = useState('');
  const [newFavoriteInput, setNewFavoriteInput] = useState('');
  const [newBeenThereInput, setNewBeenThereInput] = useState('');
  const [newDreamInput, setNewDreamInput] = useState('');
  const [newLovedInput, setNewLovedInput] = useState('');
  const [newAvoidInput, setNewAvoidInput] = useState('');
  const [destRestrictionError, setDestRestrictionError] = useState('');
  const [prefsChanged, setPrefsChanged] = useState(false);

  // Home airports state — read from canonical preferences JSONB
  const [homeAirports, setHomeAirports] = useState(() => {
    const raw = _prefs.homeAirports;
    if (Array.isArray(raw)) return raw;
    if (typeof raw === 'string') { try { return JSON.parse(raw); } catch { return []; } }
    return [];
  });
  const [airportQuery, setAirportQuery] = useState('');
  const [airportResults, setAirportResults] = useState([]);
  const [airportSearching, setAirportSearching] = useState(false);
  const [showAirportSearch, setShowAirportSearch] = useState(false);
  const airportSearchRef = useRef(null);
  const airportDebounceRef = useRef(null);

  // Cabin class state — canonical preferences.cabinClass
  const [cabinClass, setCabinClass] = useState(_prefs.cabinClass || 'ECONOMY');

  // Airport search via backend Amadeus proxy
  const searchAirports = useCallback(async (keyword) => {
    if (!keyword || keyword.length < 2) {
      setAirportResults([]);
      return;
    }
    setAirportSearching(true);
    try {
      const token = (await (await import('../../lib/supabase')).default.auth.getSession()).data.session?.access_token;
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';
      const res = await fetch(`${backendUrl}/api/amadeus/airports/search?keyword=${encodeURIComponent(keyword)}&limit=8`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        // Filter out airports already added
        const existingCodes = homeAirports.map(a => a.code);
        setAirportResults((data.results || []).filter(a => !existingCodes.includes(a.iataCode)));
      }
    } catch (err) {
      console.error('Airport search failed:', err);
    } finally {
      setAirportSearching(false);
    }
  }, [homeAirports]);

  // Debounced airport search
  useEffect(() => {
    if (airportDebounceRef.current) clearTimeout(airportDebounceRef.current);
    if (airportQuery.length >= 2) {
      airportDebounceRef.current = setTimeout(() => searchAirports(airportQuery), 300);
    } else {
      setAirportResults([]);
    }
    return () => { if (airportDebounceRef.current) clearTimeout(airportDebounceRef.current); };
  }, [airportQuery, searchAirports]);

  // Click outside to close airport dropdown
  useEffect(() => {
    const handler = (e) => {
      if (airportSearchRef.current && !airportSearchRef.current.contains(e.target)) {
        setShowAirportSearch(false);
        setAirportQuery('');
        setAirportResults([]);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const addAirport = async (airport) => {
    if (homeAirports.length >= 5) {
      setMessage({ type: 'error', text: 'Maximum 5 airports allowed' });
      return;
    }
    const newAirport = {
      code: airport.iataCode,
      name: airport.name,
      city: airport.city,
      country: airport.country,
    };
    const updated = [...homeAirports, newAirport];
    setHomeAirports(updated);
    setAirportQuery('');
    setAirportResults([]);
    setShowAirportSearch(false);
    try {
      await updateProfile({ home_airports: updated });
      setMessage({ type: 'success', text: `${airport.iataCode} added` });
    } catch {
      setMessage({ type: 'error', text: 'Failed to save airport' });
      setHomeAirports(homeAirports); // revert
    }
  };

  const removeAirport = async (code) => {
    const updated = homeAirports.filter(a => a.code !== code);
    setHomeAirports(updated);
    try {
      await updateProfile({ home_airports: updated });
      setMessage({ type: 'success', text: `${code} removed` });
    } catch {
      setMessage({ type: 'error', text: 'Failed to save' });
      setHomeAirports(homeAirports); // revert
    }
  };

  const saveCabinClass = async (value) => {
    setCabinClass(value);
    try {
      await updateProfile({ preferred_cabin_class: value });
      setMessage({ type: 'success', text: 'Flight preference updated' });
    } catch {
      setMessage({ type: 'error', text: 'Failed to save preference' });
    }
  };

  // Track changes
  const updateTravelPref = (key, value) => {
    setTravelPrefs(prev => ({ ...prev, [key]: value }));
    setPrefsChanged(true);
  };

  const toggleTravelPrefArray = (key, item) => {
    setTravelPrefs(prev => ({
      ...prev,
      [key]: prev[key].includes(item)
        ? prev[key].filter(i => i !== item)
        : [...prev[key], item]
    }));
    setPrefsChanged(true);
  };

  const saveTravelPrefs = async () => {
    setIsLoading(true);
    try {
      const toSave = { ...travelPrefs };
      // safety_considerations is stored as JSON string in DB
      if (toSave.safety_considerations) {
        toSave.safety_considerations = JSON.stringify(toSave.safety_considerations);
      }
      // passport_countries is stored as JSON string in DB
      if (toSave.passport_countries) {
        toSave.passport_countries = JSON.stringify(toSave.passport_countries);
      }
      await updateProfile(toSave);
      setMessage({ type: 'success', text: 'Travel preferences saved' });
      setPrefsChanged(false);
    } catch {
      setMessage({ type: 'error', text: 'Failed to save preferences' });
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  const handleAnalyticsToggle = async (enabled) => {
    setIsLoading(true);
    try {
      await updateAnalyticsConsent(enabled);
      setAnalyticsConsent(enabled);
      setMessage({ type: 'success', text: `Analytics ${enabled ? 'enabled' : 'disabled'}` });
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to update preference' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleExportData = async () => {
    setIsLoading(true);
    setMessage(null);
    try {
      const reason = exportReason === 'other' ? (exportReasonText || 'other') : (exportReason || 'not specified');
      const data = await exportData(reason);

      // Create and download JSON file
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `arrivo-data-export-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      setMessage({ type: 'success', text: 'Data exported successfully!' });
      setExportReason('');
      setExportReasonText('');
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to export data. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'DELETE') {
      setMessage({ type: 'error', text: 'Please type DELETE to confirm' });
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || ''}/api/delete-account`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${(await (await import('../../lib/supabase')).default.auth.getSession()).data.session?.access_token}`
        }
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.message || 'Failed to delete account');
      }

      setMessage({
        type: 'info',
        text: 'Your account and all associated data have been permanently deleted.'
      });

      // Sign out after a short delay so user sees the message
      setTimeout(async () => {
        await signOut();
        onClose();
      }, 2000);
    } catch (err) {
      console.error('Account deletion error:', err);
      setMessage({ type: 'error', text: err.message || 'Failed to delete account. Please contact support.' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveCommunitySettings = async (newSettings) => {
    await updateProfile({ privacy_settings: newSettings });
    setCommunitySettings(newSettings);
    setMessage({ type: 'success', text: 'Community settings saved' });
  };

  const tabs = [
    { id: 'preferences', label: 'General', Icon: Settings },
    { id: 'travel', label: 'Travel', Icon: Plane },
    { id: 'billing', label: 'Billing', Icon: CreditCard },
    { id: 'community', label: 'Wanderer', Icon: Users },
    { id: 'privacy', label: 'Privacy', Icon: Lock },
    { id: 'data', label: 'Your Data', Icon: Database },
  ];

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="overflow-hidden flex flex-col max-w-2xl w-full" style={{ height: '85vh', background: 'white', borderRadius: '22px', border: '1px solid rgba(31,29,59,0.08)', boxShadow: '0 22px 60px rgba(28,41,53,0.12)' }}>
        {/* Header */}
        <div className="px-6 py-4 flex items-center justify-between" style={{ borderBottom: '1px solid rgba(31,29,59,0.08)' }}>
          <h2 className="text-xl" style={{ ...FONTS.headingBold, color: '#313143' }}>Settings</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-100 overflow-x-auto scrollbar-hide">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 px-3 py-3 text-sm transition-colors flex items-center justify-center gap-1.5 whitespace-nowrap ${
                activeTab === tab.id
                  ? 'text-teal-600 border-b-2 border-teal-500'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              style={FONTS.bodyBold}
            >
              <tab.Icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content — flex-1 fills remaining space; parent minHeight prevents shrinking */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Message */}
          {message && (
            <div className={`mb-4 p-4 rounded-xl text-sm ${
              message.type === 'success' ? 'bg-teal-50 text-teal-800 border border-teal-200' :
              message.type === 'error' ? 'bg-red-50 text-red-800 border border-red-200' :
              'bg-blue-50 text-blue-800 border border-blue-200'
            }`} style={FONTS.body}>
              {message.text}
            </div>
          )}

          {/* Preferences Tab */}
          {activeTab === 'preferences' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg text-gray-900 mb-4" style={FONTS.headingSemibold}>Display Preferences</h3>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm text-gray-700 mb-1" style={FONTS.body}>
                      Currency
                    </label>
                    <select
                      value={profile?.currency || 'USD'}
                      onChange={async (e) => {
                        try {
                          await updateProfile({ currency: e.target.value });
                          setMessage({ type: 'success', text: 'Currency updated' });
                        } catch {
                          setMessage({ type: 'error', text: 'Failed to update' });
                        }
                      }}
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-teal-100 focus:border-teal-500 outline-none transition-all text-sm text-gray-800"
                      style={FONTS.body}
                    >
                      <option value="USD">USD ($)</option>
                      <option value="EUR">EUR (&euro;)</option>
                      <option value="GBP">GBP (&pound;)</option>
                      <option value="JPY">JPY (&yen;)</option>
                      <option value="AUD">AUD ($)</option>
                      <option value="CAD">CAD ($)</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm text-gray-700 mb-1" style={FONTS.body}>
                      Default Trip Length (days)
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="90"
                      value={profile?.default_trip_length || 7}
                      onChange={async (e) => {
                        try {
                          await updateProfile({ default_trip_length: parseInt(e.target.value) });
                          setMessage({ type: 'success', text: 'Updated' });
                        } catch {
                          setMessage({ type: 'error', text: 'Failed to update' });
                        }
                      }}
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-teal-100 focus:border-teal-500 outline-none transition-all text-sm text-gray-800"
                      style={FONTS.body}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Travel Preferences Tab */}
          {activeTab === 'travel' && (
            <div className="space-y-6">

              {/* Home City */}
              <div>
                <h3 className="text-lg text-gray-900 mb-2" style={FONTS.headingSemibold}>🏠 Home City</h3>
                <p className="text-xs text-gray-500 mb-3" style={FONTS.body}>
                  Auto-fills your departure city and calculates flight times
                </p>
                <input
                  type="text"
                  value={travelPrefs.home_city || ''}
                  onChange={(e) => updateTravelPref('home_city', e.target.value)}
                  placeholder="e.g. New York, London, Tokyo"
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-teal-100 focus:border-teal-500 outline-none text-sm"
                  style={FONTS.body}
                />
              </div>

              {/* Home Airports */}
              {features.AMADEUS_AIRPORTS && (
                <div>
                  <h3 className="text-lg text-gray-900 mb-2" style={FONTS.headingSemibold}>Home Airports</h3>
                  <p className="text-xs text-gray-500 mb-3" style={FONTS.body}>
                    We'll search all your airports to find the best flight options for each trip. Different from your home city — airports are used to find the best flight prices and connections.
                  </p>

                  {/* Airport chips */}
                  {homeAirports.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-3">
                      {homeAirports.map(airport => (
                        <div
                          key={airport.code}
                          className="flex items-center gap-2 px-3 py-2 bg-teal-50 rounded-xl border border-teal-200"
                        >
                          <MapPin className="w-4 h-4 text-teal-600 flex-shrink-0" />
                          <div className="min-w-0">
                            <div className="flex items-center gap-1.5">
                              <span className="text-sm font-bold text-gray-800" style={FONTS.headingBold}>{airport.code}</span>
                              <span className="text-xs text-gray-400">—</span>
                              <span className="text-xs text-gray-600 truncate" style={FONTS.body}>{airport.name}</span>
                            </div>
                            <span className="text-xs text-gray-400" style={FONTS.body}>{airport.city}{airport.country ? `, ${airport.country}` : ''}</span>
                          </div>
                          <button
                            onClick={() => removeAirport(airport.code)}
                            className="ml-1 p-1 hover:bg-teal-100 rounded-full transition-colors"
                          >
                            <X className="w-3.5 h-3.5 text-gray-400 hover:text-gray-600" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Add airport */}
                  {homeAirports.length < 5 && (
                    <div className="relative" ref={airportSearchRef}>
                      {!showAirportSearch ? (
                        <button
                          onClick={() => setShowAirportSearch(true)}
                          className="flex items-center gap-2 px-4 py-2.5 border-2 border-dashed border-gray-300 rounded-xl hover:border-teal-400 hover:bg-teal-50 transition-all w-full text-left"
                          style={FONTS.body}
                        >
                          <Search className="w-4 h-4 text-gray-400" />
                          <span className="text-sm text-gray-500">Add an airport...</span>
                          <span className="text-xs text-gray-400 ml-auto">{homeAirports.length}/5</span>
                        </button>
                      ) : (
                        <div>
                          <div className="flex items-center gap-2 px-4 py-2.5 border-2 border-teal-500 rounded-xl bg-white">
                            <Search className="w-4 h-4 text-teal-500" />
                            <input
                              type="text"
                              value={airportQuery}
                              onChange={(e) => setAirportQuery(e.target.value)}
                              placeholder="Search airports (e.g., JFK, London, Tokyo)..."
                              className="flex-1 outline-none text-sm text-gray-800"
                              style={FONTS.body}
                              autoFocus
                            />
                            {airportSearching && (
                              <div className="w-4 h-4 border-2 border-teal-400 border-t-transparent rounded-full animate-spin" />
                            )}
                          </div>

                          {/* Dropdown results */}
                          {airportResults.length > 0 && (
                            <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-xl shadow-lg border border-gray-200 z-20 max-h-64 overflow-y-auto">
                              {airportResults.map(airport => (
                                <button
                                  key={airport.iataCode}
                                  onClick={() => addAirport(airport)}
                                  className="w-full px-4 py-3 text-left hover:bg-teal-50 flex items-center gap-3 border-b border-gray-50 last:border-b-0 transition-colors"
                                >
                                  <span
                                    className="text-sm font-bold text-gray-800 bg-gray-100 px-2 py-1 rounded"
                                    style={FONTS.headingBold}
                                  >
                                    {airport.iataCode}
                                  </span>
                                  <div className="min-w-0">
                                    <div className="text-sm text-gray-700 truncate" style={FONTS.body}>{airport.name}</div>
                                    <div className="text-xs text-gray-400" style={FONTS.body}>{airport.city}{airport.country ? `, ${airport.country}` : ''}</div>
                                  </div>
                                  <span className="text-xs text-gray-400 ml-auto capitalize">{(airport.type || '').toLowerCase()}</span>
                                </button>
                              ))}
                            </div>
                          )}

                          {airportQuery.length >= 2 && !airportSearching && airportResults.length === 0 && (
                            <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-xl shadow-lg border border-gray-200 z-20 p-4 text-center">
                              <p className="text-sm text-gray-500" style={FONTS.body}>No airports found for "{airportQuery}"</p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {homeAirports.length >= 5 && (
                    <p className="text-xs text-gray-400 mt-1" style={FONTS.body}>Maximum 5 airports reached. Remove one to add another.</p>
                  )}
                </div>
              )}

              {/* Flight Preferences */}
              {features.AMADEUS_FLIGHTS && (
                <div>
                  <h3 className="text-lg text-gray-900 mb-2" style={FONTS.headingSemibold}>Flight Preferences</h3>
                  <p className="text-xs text-gray-500 mb-3" style={FONTS.body}>
                    Default cabin class for flight searches. You can override per flight in your itinerary.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {[
                      { value: 'ECONOMY', label: 'Economy' },
                      { value: 'PREMIUM_ECONOMY', label: 'Premium Economy' },
                      { value: 'BUSINESS', label: 'Business' },
                      { value: 'FIRST', label: 'First' },
                    ].map(option => (
                      <button
                        key={option.value}
                        onClick={() => saveCabinClass(option.value)}
                        className={BTN.chip(cabinClass === option.value)}
                        style={FONTS.body}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {(features.AMADEUS_AIRPORTS || features.AMADEUS_FLIGHTS) && <hr className="border-gray-100" />}

              {/* Travel Style */}
              <div>
                <h3 className="text-lg text-gray-900 mb-4" style={FONTS.headingSemibold}>Travel Style</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm text-gray-700 mb-2" style={FONTS.body}>Who do you usually travel with?</label>
                    <p className="text-xs text-gray-500 mb-2">Pick a base type plus any modifiers (kids, elderly, pets, etc.)</p>
                    <div className="flex flex-wrap gap-2">
                      {CANONICAL_COMPANION_OPTIONS.map(option => {
                        const current = Array.isArray(travelPrefs.travel_companions)
                          ? travelPrefs.travel_companions
                          : (travelPrefs.travel_companions ? [travelPrefs.travel_companions] : []);
                        const selected = current.includes(option.value);
                        const baseValues = CANONICAL_COMPANION_OPTIONS.filter(o => o.base).map(o => o.value);
                        return (
                          <button
                            key={option.value}
                            onClick={() => {
                              let updated;
                              if (selected) {
                                updated = current.filter(c => c !== option.value);
                              } else if (option.base) {
                                updated = [...current.filter(c => !baseValues.includes(c)), option.value];
                              } else {
                                updated = [...current, option.value];
                              }
                              updateTravelPref('travel_companions', updated);
                            }}
                            className={BTN.chip(selected)}
                            style={FONTS.body}
                          >
                            {option.icon ? `${option.icon} ` : ''}{option.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm text-gray-700 mb-2" style={FONTS.body}>What's your travel pace?</label>
                    <div className="flex flex-wrap gap-2">
                      {[
                        { value: 'relaxed', label: 'Relaxed', desc: '2-3 activities/day' },
                        { value: 'moderate', label: 'Moderate', desc: '3-4 activities/day' },
                        { value: 'packed', label: 'Packed', desc: '5+ activities/day' },
                      ].map(option => (
                        <button
                          key={option.value}
                          onClick={() => updateTravelPref('travel_pace', option.value)}
                          className={`flex-1 min-w-[100px] ${BTN.chip(travelPrefs.travel_pace === option.value)}`}
                          style={FONTS.body}
                        >
                          <div className="font-medium">{option.label}</div>
                          <div className="text-xs text-gray-500 mt-0.5">{option.desc}</div>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm text-gray-700 mb-2" style={FONTS.body}>Default budget level?</label>
                    <div className="flex flex-wrap gap-2">
                      {[
                        { value: 'budget', label: 'Budget' },
                        { value: 'moderate', label: 'Moderate' },
                        { value: 'luxury', label: 'Luxury' },
                      ].map(option => (
                        <button
                          key={option.value}
                          onClick={() => updateTravelPref('default_budget', option.value)}
                          className={BTN.chip(travelPrefs.default_budget === option.value)}
                          style={FONTS.body}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Interests */}
              <div>
                <h3 className="text-lg text-gray-900 mb-3" style={FONTS.headingSemibold}>Interests</h3>
                <p className="text-sm text-gray-500 mb-3" style={FONTS.body}>Select all that apply - we'll prioritize these in your itineraries</p>
                <div className="flex flex-wrap gap-2">
                  {[
                    'Museums', 'Hiking', 'Beaches', 'Architecture',
                    'Street Food', 'Fine Dining', 'Wine/Beer',
                    'Photography', 'History', 'Art', 'Music',
                    'Sports', 'Wellness/Spa', 'Local Markets'
                  ].map(interest => (
                    <button
                      key={interest}
                      onClick={() => toggleTravelPrefArray('interests', interest)}
                      className={BTN.chip(travelPrefs.interests.includes(interest))}
                      style={FONTS.body}
                    >
                      {interest}
                    </button>
                  ))}
                </div>
              </div>

              <p className="text-xs text-gray-500 italic bg-gray-50 rounded-lg px-3 py-2">
                The sections below may include health, dietary, religious, or identity-related info. It's optional, never shared, and only used to personalize your trips.
              </p>

              {/* Dietary Restrictions */}
              <div>
                <h3 className="text-lg text-gray-900 mb-3" style={FONTS.headingSemibold}>Dietary Restrictions</h3>
                <div className="flex flex-wrap gap-2">
                  {[
                    'Vegetarian', 'Vegan', 'Pescatarian', 'Gluten-Free',
                    'Dairy-Free', 'Nut Allergies', 'Shellfish/Fish Allergy',
                    'Low-Carb/Keto', 'Diabetic Diet', 'Kosher', 'Halal', 'None'
                  ].map(diet => (
                    <button
                      key={diet}
                      onClick={() => {
                        if (diet === 'None') {
                          updateTravelPref('dietary_restrictions',
                            travelPrefs.dietary_restrictions.includes('None') ? [] : ['None']
                          );
                        } else {
                          const current = travelPrefs.dietary_restrictions;
                          const updated = current.includes(diet)
                            ? current.filter(d => d !== diet)
                            : [...current.filter(d => d !== 'None'), diet];
                          updateTravelPref('dietary_restrictions', updated);
                        }
                      }}
                      className={BTN.chip(travelPrefs.dietary_restrictions.includes(diet))}
                      style={FONTS.body}
                    >
                      {diet}
                    </button>
                  ))}
                </div>
              </div>

              {/* Cuisine Preferences */}
              <div>
                <h3 className="text-lg text-gray-900 mb-3" style={FONTS.headingSemibold}>Cuisine Preferences</h3>

                {/* Liked cuisines */}
                <div className="mb-4">
                  <label className="block text-sm text-gray-700 mb-2" style={FONTS.body}>Cuisines you enjoy</label>
                  <div className="flex flex-wrap gap-2 mb-2">
                    {[
                      'Italian', 'Japanese', 'Mexican', 'Thai', 'Indian', 'Chinese',
                      'French', 'Korean', 'Vietnamese', 'Mediterranean', 'Middle Eastern',
                      'American', 'Spanish', 'Greek', 'Turkish', 'Brazilian', 'Ethiopian'
                    ].map(cuisine => (
                      <button
                        key={cuisine}
                        onClick={() => toggleTravelPrefArray('liked_cuisines', cuisine)}
                        className={BTN.chip(travelPrefs.liked_cuisines.includes(cuisine))}
                        style={FONTS.body}
                      >
                        {cuisine}
                      </button>
                    ))}
                    {/* Show custom-added cuisines (not in preset list) */}
                    {travelPrefs.liked_cuisines
                      .filter(c => ![
                        'Italian', 'Japanese', 'Mexican', 'Thai', 'Indian', 'Chinese',
                        'French', 'Korean', 'Vietnamese', 'Mediterranean', 'Middle Eastern',
                        'American', 'Spanish', 'Greek', 'Turkish', 'Brazilian', 'Ethiopian'
                      ].includes(c))
                      .map(cuisine => (
                        <button
                          key={cuisine}
                          onClick={() => toggleTravelPrefArray('liked_cuisines', cuisine)}
                          className={BTN.chip(true)}
                          style={FONTS.body}
                        >
                          {cuisine} ✕
                        </button>
                      ))
                    }
                  </div>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={newCuisineInput}
                      onChange={(e) => setNewCuisineInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && newCuisineInput.trim()) {
                          e.preventDefault();
                          if (!travelPrefs.liked_cuisines.includes(newCuisineInput.trim())) {
                            toggleTravelPrefArray('liked_cuisines', newCuisineInput.trim());
                          }
                          setNewCuisineInput('');
                        }
                      }}
                      placeholder="Add other cuisine..."
                      className="flex-1 px-3 py-1.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-teal-100 focus:border-teal-500 outline-none text-sm"
                      style={FONTS.body}
                    />
                    <button
                      onClick={() => {
                        if (newCuisineInput.trim() && !travelPrefs.liked_cuisines.includes(newCuisineInput.trim())) {
                          toggleTravelPrefArray('liked_cuisines', newCuisineInput.trim());
                          setNewCuisineInput('');
                        }
                      }}
                      className="px-3 py-1.5 bg-[#313143] text-white rounded-xl hover:bg-[#3d3d52] text-sm transition-colors"
                      style={FONTS.bodyBold}
                    >
                      Add
                    </button>
                  </div>
                </div>

                {/* Disliked cuisines */}
                <div>
                  <label className="block text-sm text-gray-700 mb-2" style={FONTS.body}>Cuisines to avoid</label>
                  {travelPrefs.disliked_cuisines.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-2">
                      {travelPrefs.disliked_cuisines.map((cuisine, idx) => (
                        <span
                          key={idx}
                          className="inline-flex items-center gap-1 px-3 py-1 bg-amber-100 text-amber-700 rounded-full text-sm"
                          style={FONTS.body}
                        >
                          {cuisine}
                          <button
                            onClick={() => toggleTravelPrefArray('disliked_cuisines', cuisine)}
                            className="ml-1 hover:text-red-900"
                          >
                            x
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={newDislikedCuisineInput}
                      onChange={(e) => setNewDislikedCuisineInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && newDislikedCuisineInput.trim()) {
                          e.preventDefault();
                          if (!travelPrefs.disliked_cuisines.includes(newDislikedCuisineInput.trim())) {
                            toggleTravelPrefArray('disliked_cuisines', newDislikedCuisineInput.trim());
                          }
                          setNewDislikedCuisineInput('');
                        }
                      }}
                      placeholder="Add cuisine to avoid..."
                      className="flex-1 px-3 py-1.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-teal-100 focus:border-teal-500 outline-none text-sm"
                      style={FONTS.body}
                    />
                    <button
                      onClick={() => {
                        if (newDislikedCuisineInput.trim() && !travelPrefs.disliked_cuisines.includes(newDislikedCuisineInput.trim())) {
                          toggleTravelPrefArray('disliked_cuisines', newDislikedCuisineInput.trim());
                          setNewDislikedCuisineInput('');
                        }
                      }}
                      className="px-3 py-1.5 bg-[#313143] text-white rounded-xl hover:bg-[#3d3d52] text-sm transition-colors"
                      style={FONTS.bodyBold}
                    >
                      Add
                    </button>
                  </div>
                </div>
              </div>

              {/* Accessibility Needs */}
              <div>
                <h3 className="text-lg text-gray-900 mb-3" style={FONTS.headingSemibold}>Accessibility Needs</h3>
                <div className="flex flex-wrap gap-2">
                  {[
                    'Wheelchair accessible',
                    'Limited mobility',
                    'Visual impairment',
                    'Hearing impairment',
                    'Service animal'
                  ].map(need => (
                    <button
                      key={need}
                      onClick={() => toggleTravelPrefArray('accessibility_needs', need)}
                      className={BTN.chip(travelPrefs.accessibility_needs.includes(need))}
                      style={FONTS.body}
                    >
                      {need}
                    </button>
                  ))}
                </div>
              </div>

              {/* Safety & Comfort */}
              <div>
                <h3 className="text-lg text-gray-900 mb-2" style={FONTS.headingSemibold}>Safety & Comfort</h3>
                <p className="text-xs text-gray-500 mb-3" style={FONTS.body}>
                  We'll prioritize destinations and accommodations known to be welcoming and safe.
                </p>
                <div className="flex flex-wrap gap-2 mb-3">
                  {[
                    { value: 'lgbtq', label: 'LGBTQ+ friendly destinations preferred' },
                    { value: 'solo_female', label: 'Solo female traveler safety' },
                    { value: 'religious', label: 'Religious/cultural considerations' },
                  ].map(option => (
                    <button
                      key={option.value}
                      onClick={() => toggleTravelPrefArray('safety_considerations', option.value)}
                      className={BTN.chip(travelPrefs.safety_considerations.includes(option.value))}
                      style={FONTS.body}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
                <input
                  type="text"
                  value={travelPrefs.safety_notes}
                  onChange={(e) => updateTravelPref('safety_notes', e.target.value)}
                  placeholder="Any other safety or comfort notes (optional)"
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-teal-100 focus:border-teal-500 outline-none text-sm"
                  style={FONTS.body}
                />
              </div>

              {/* Passport Countries */}
              <div>
                <h3 className="text-lg text-gray-900 mb-2" style={FONTS.headingSemibold}>Passport Countries</h3>
                <p className="text-xs text-gray-500 mb-3" style={FONTS.body}>
                  Used for visa requirements and entry restrictions
                </p>
                {travelPrefs.passport_countries.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-2">
                    {travelPrefs.passport_countries.map((country, idx) => (
                      <span
                        key={idx}
                        className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm"
                        style={FONTS.body}
                      >
                        {country}
                        <button
                          onClick={() => {
                            updateTravelPref('passport_countries',
                              travelPrefs.passport_countries.filter((_, i) => i !== idx)
                            );
                          }}
                          className="ml-1 hover:text-blue-900"
                        >
                          x
                        </button>
                      </span>
                    ))}
                  </div>
                )}
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newPassportInput}
                    onChange={(e) => setNewPassportInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && newPassportInput.trim()) {
                        e.preventDefault();
                        if (!travelPrefs.passport_countries.includes(newPassportInput.trim())) {
                          updateTravelPref('passport_countries', [...travelPrefs.passport_countries, newPassportInput.trim()]);
                        }
                        setNewPassportInput('');
                      }
                    }}
                    placeholder="e.g., United States, United Kingdom..."
                    className="flex-1 px-3 py-1.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-teal-100 focus:border-teal-500 outline-none text-sm"
                    style={FONTS.body}
                  />
                  <button
                    onClick={() => {
                      if (newPassportInput.trim() && !travelPrefs.passport_countries.includes(newPassportInput.trim())) {
                        updateTravelPref('passport_countries', [...travelPrefs.passport_countries, newPassportInput.trim()]);
                        setNewPassportInput('');
                      }
                    }}
                    className="px-3 py-1.5 bg-[#313143] text-white rounded-xl hover:bg-[#3d3d52] text-sm transition-colors"
                    style={FONTS.bodyBold}
                  >
                    Add
                  </button>
                </div>
              </div>

              {/* Destinations */}
              <div>
                <h3 className="text-lg text-gray-900 mb-3" style={FONTS.headingSemibold}>Destinations</h3>

                {destRestrictionError && (
                  <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700" style={FONTS.body}>
                    {destRestrictionError}
                  </div>
                )}

                {/* Dream Destinations */}
                <div className="mb-4">
                  <label className="block text-sm text-gray-700 mb-1" style={FONTS.body}>Dream Destinations</label>
                  <p className="text-xs text-gray-500 mb-2">Places you dream of visiting</p>
                  {travelPrefs.dream_destinations.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-2">
                      {travelPrefs.dream_destinations.map((dest, idx) => (
                        <span
                          key={idx}
                          className="inline-flex items-center gap-1 px-3 py-1 bg-teal-100 text-teal-700 rounded-full text-sm"
                          style={FONTS.body}
                        >
                          {dest}
                          <button
                            onClick={() => {
                              updateTravelPref('dream_destinations',
                                travelPrefs.dream_destinations.filter((_, i) => i !== idx)
                              );
                            }}
                            className="ml-1 hover:text-teal-900"
                          >
                            x
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={newDreamInput}
                      onChange={(e) => setNewDreamInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && newDreamInput.trim()) {
                          e.preventDefault();
                          const restriction = checkDestinationRestriction(newDreamInput.trim());
                          if (restriction && !restriction.allowed) {
                            setDestRestrictionError(restriction.message); setTimeout(() => setDestRestrictionError(''), 5000);
                            return;
                          }
                          if (!travelPrefs.dream_destinations.includes(newDreamInput.trim())) {
                            updateTravelPref('dream_destinations', [...travelPrefs.dream_destinations, newDreamInput.trim()]);
                          }
                          setNewDreamInput('');
                        }
                      }}
                      placeholder="e.g., Tokyo, Paris, Bali..."
                      className="flex-1 px-3 py-1.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-teal-100 focus:border-teal-500 outline-none text-sm"
                      style={FONTS.body}
                    />
                    <button
                      onClick={() => {
                        if (newDreamInput.trim()) {
                          const restriction = checkDestinationRestriction(newDreamInput.trim());
                          if (restriction && !restriction.allowed) {
                            setDestRestrictionError(restriction.message); setTimeout(() => setDestRestrictionError(''), 5000);
                            return;
                          }
                          if (!travelPrefs.dream_destinations.includes(newDreamInput.trim())) {
                            updateTravelPref('dream_destinations', [...travelPrefs.dream_destinations, newDreamInput.trim()]);
                          }
                          setNewDreamInput('');
                        }
                      }}
                      className="px-3 py-1.5 bg-[#313143] text-white rounded-xl hover:bg-[#3d3d52] text-sm transition-colors"
                      style={FONTS.bodyBold}
                    >
                      Add
                    </button>
                  </div>
                </div>

                {/* Places You've Loved */}
                <div className="mb-4">
                  <label className="block text-sm text-gray-700 mb-1" style={FONTS.body}>Places You've Loved</label>
                  <p className="text-xs text-gray-500 mb-2">Destinations you've been to and loved (helps AI understand your taste)</p>
                  {travelPrefs.loved_places.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-2">
                      {travelPrefs.loved_places.map((dest, idx) => (
                        <span
                          key={idx}
                          className="inline-flex items-center gap-1 px-3 py-1 bg-pink-100 text-pink-700 rounded-full text-sm"
                          style={FONTS.body}
                        >
                          {dest}
                          <button
                            onClick={() => {
                              updateTravelPref('loved_places',
                                travelPrefs.loved_places.filter((_, i) => i !== idx)
                              );
                            }}
                            className="ml-1 hover:text-pink-900"
                          >
                            x
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={newLovedInput}
                      onChange={(e) => setNewLovedInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && newLovedInput.trim()) {
                          e.preventDefault();
                          const restriction = checkDestinationRestriction(newLovedInput.trim());
                          if (restriction && !restriction.allowed) {
                            setDestRestrictionError(restriction.message); setTimeout(() => setDestRestrictionError(''), 5000);
                            return;
                          }
                          if (!travelPrefs.loved_places.includes(newLovedInput.trim())) {
                            updateTravelPref('loved_places', [...travelPrefs.loved_places, newLovedInput.trim()]);
                          }
                          setNewLovedInput('');
                        }
                      }}
                      placeholder="e.g., Barcelona, Kyoto..."
                      className="flex-1 px-3 py-1.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-teal-100 focus:border-teal-500 outline-none text-sm"
                      style={FONTS.body}
                    />
                    <button
                      onClick={() => {
                        if (newLovedInput.trim()) {
                          const restriction = checkDestinationRestriction(newLovedInput.trim());
                          if (restriction && !restriction.allowed) {
                            setDestRestrictionError(restriction.message); setTimeout(() => setDestRestrictionError(''), 5000);
                            return;
                          }
                          if (!travelPrefs.loved_places.includes(newLovedInput.trim())) {
                            updateTravelPref('loved_places', [...travelPrefs.loved_places, newLovedInput.trim()]);
                          }
                          setNewLovedInput('');
                        }
                      }}
                      className="px-3 py-1.5 bg-[#313143] text-white rounded-xl hover:bg-[#3d3d52] text-sm transition-colors"
                      style={FONTS.bodyBold}
                    >
                      Add
                    </button>
                  </div>
                </div>

                {/* Places to Avoid */}
                <div>
                  <label className="block text-sm text-gray-700 mb-1" style={FONTS.body}>Places to Avoid</label>
                  <p className="text-xs text-gray-500 mb-2">Destinations or types of places you want to avoid</p>
                  {travelPrefs.avoid_places.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-2">
                      {travelPrefs.avoid_places.map((dest, idx) => (
                        <span
                          key={idx}
                          className="inline-flex items-center gap-1 px-3 py-1 bg-red-100 text-red-600 rounded-full text-sm"
                          style={FONTS.body}
                        >
                          {dest}
                          <button
                            onClick={() => {
                              updateTravelPref('avoid_places',
                                travelPrefs.avoid_places.filter((_, i) => i !== idx)
                              );
                            }}
                            className="ml-1 hover:text-red-900"
                          >
                            x
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={newAvoidInput}
                      onChange={(e) => setNewAvoidInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && newAvoidInput.trim()) {
                          e.preventDefault();
                          const restriction = checkDestinationRestriction(newAvoidInput.trim());
                          if (restriction && !restriction.allowed) {
                            setDestRestrictionError(restriction.message); setTimeout(() => setDestRestrictionError(''), 5000);
                            return;
                          }
                          if (!travelPrefs.avoid_places.includes(newAvoidInput.trim())) {
                            updateTravelPref('avoid_places', [...travelPrefs.avoid_places, newAvoidInput.trim()]);
                          }
                          setNewAvoidInput('');
                        }
                      }}
                      placeholder="e.g., Las Vegas, crowded resorts..."
                      className="flex-1 px-3 py-1.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-teal-100 focus:border-teal-500 outline-none text-sm"
                      style={FONTS.body}
                    />
                    <button
                      onClick={() => {
                        if (newAvoidInput.trim()) {
                          const restriction = checkDestinationRestriction(newAvoidInput.trim());
                          if (restriction && !restriction.allowed) {
                            setDestRestrictionError(restriction.message); setTimeout(() => setDestRestrictionError(''), 5000);
                            return;
                          }
                          if (!travelPrefs.avoid_places.includes(newAvoidInput.trim())) {
                            updateTravelPref('avoid_places', [...travelPrefs.avoid_places, newAvoidInput.trim()]);
                          }
                          setNewAvoidInput('');
                        }
                      }}
                      className="px-3 py-1.5 bg-[#313143] text-white rounded-xl hover:bg-[#3d3d52] text-sm transition-colors"
                      style={FONTS.bodyBold}
                    >
                      Add
                    </button>
                  </div>
                </div>
              </div>

              {/* Travel Notes */}
              <div>
                <h3 className="text-lg text-gray-900 mb-2" style={FONTS.headingSemibold}>Anything Else?</h3>
                <textarea
                  value={travelPrefs.travel_notes}
                  onChange={(e) => updateTravelPref('travel_notes', e.target.value)}
                  placeholder="e.g., I prefer morning activities, hate crowded places, always need good coffee nearby..."
                  rows={3}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-teal-100 focus:border-teal-500 outline-none text-sm resize-none"
                  style={FONTS.body}
                />
              </div>

              {/* Save button */}
              {prefsChanged && (
                <div className="sticky bottom-0 bg-white pt-3 pb-1 border-t border-gray-100">
                  <button
                    onClick={saveTravelPrefs}
                    disabled={isLoading}
                    className="w-full px-6 py-3 bg-[#313143] text-white rounded-xl hover:bg-[#3d3d52] transition-colors disabled:opacity-50"
                    style={FONTS.bodyBold}
                  >
                    {isLoading ? 'Saving...' : 'Save Preferences'}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Billing Tab */}
          {activeTab === 'billing' && (
            <div className="space-y-6">
              {/* Current Plan Card */}
              <div className="bg-gradient-to-br from-teal-50 to-teal-100 rounded-2xl p-6 border border-teal-100">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <span className={`px-3 py-1 text-xs rounded-full ${
                      profile?.tier === 'planner' ? 'bg-purple-100 text-purple-700' :
                      profile?.tier === 'admin' ? 'bg-amber-100 text-amber-700' :
                      'bg-teal-100 text-teal-700'
                    }`} style={FONTS.bodyBold}>
                      {(profile?.tier || 'explorer').toUpperCase()}
                    </span>
                    <h3 className="text-lg text-gray-900 mt-2" style={FONTS.headingBold}>
                      {tierConfig[profile?.tier || 'explorer']?.name || 'Explorer'}
                    </h3>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl text-gray-900" style={FONTS.headingBold}>
                      {tierConfig[profile?.tier || 'explorer']?.priceDisplay || '$0/month'}
                    </div>
                  </div>
                </div>
              </div>

              {/* Payment Failed Alert */}
              {isPaymentPastDue && (
                <div className="bg-amber-50 border border-amber-300 rounded-xl p-4 flex items-start gap-3">
                  <span className="text-xl flex-shrink-0">&#9888;&#65039;</span>
                  <div>
                    <p className="text-gray-800 font-semibold text-sm" style={FONTS.bodyBold}>Payment failed</p>
                    <p className="text-gray-600 text-sm mt-1" style={FONTS.body}>
                      Your last payment couldn't be processed. Planner features are paused until payment is resolved. Update your payment method below.
                    </p>
                  </div>
                </div>
              )}

              {/* Usage Stats */}
              <div>
                <h4 className="text-gray-900 mb-4" style={FONTS.headingSemibold}>Your Usage This Quarter</h4>
                <div className="space-y-4">
                  {/* Itineraries */}
                  <div className="bg-white border border-gray-200 rounded-xl p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-gray-700" style={FONTS.body}>Itineraries Generated</span>
                      <span className="text-sm text-gray-500" style={FONTS.body}>
                        {usage?.itineraries_generated || 0} / {itineraryLimit}
                      </span>
                    </div>
                    <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all ${
                          (usage?.itineraries_generated || 0) >= (itineraryLimit)
                            ? 'bg-red-500'
                            : (usage?.itineraries_generated || 0) >= (itineraryLimit) * 0.8
                            ? 'bg-amber-500'
                            : 'bg-teal-500'
                        }`}
                        style={{
                          width: `${Math.min(100, ((usage?.itineraries_generated || 0) / (itineraryLimit)) * 100)}%`
                        }}
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1" style={FONTS.body}>Resets at start of next quarter</p>
                  </div>

                  {/* Destination Recommendations */}
                  <div className="bg-white border border-gray-200 rounded-xl p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-gray-700" style={FONTS.body}>Destination Recommendations</span>
                      <span className="text-sm text-gray-500" style={FONTS.body}>
                        {usage?.destination_recommendations || 0} / {recLimit}
                      </span>
                    </div>
                    <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all ${
                          (usage?.destination_recommendations || 0) >= (recLimit)
                            ? 'bg-red-500'
                            : (usage?.destination_recommendations || 0) >= (recLimit) * 0.8
                            ? 'bg-amber-500'
                            : 'bg-teal-500'
                        }`}
                        style={{
                          width: `${Math.min(100, ((usage?.destination_recommendations || 0) / (recLimit)) * 100)}%`
                        }}
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1" style={FONTS.body}>Resets at start of next quarter</p>
                  </div>

                  {/* Modifications info */}
                  <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-1">
                      <Info className="w-4 h-4 text-gray-500" />
                      <span className="text-sm text-gray-700" style={FONTS.bodyBold}>Chat Modifications</span>
                    </div>
                    <p className="text-sm text-gray-600" style={FONTS.body}>
                      {(getTierLimits?.() || TIER_LIMITS.explorer).modificationsPerItinerary} modifications allowed per itinerary
                    </p>
                  </div>
                </div>
              </div>

              {/* Upgrade prompt for Explorers */}
              {profile?.tier === 'explorer' && (
                <div className="bg-gradient-to-br from-teal-50 to-teal-100 rounded-2xl p-6 border border-teal-100">
                  <h4 className="text-gray-900 mb-3" style={FONTS.headingSemibold}>Upgrade to Planner</h4>
                  <ul className="space-y-2 mb-4">
                    <li className="flex items-center gap-2 text-sm text-gray-700" style={FONTS.body}>
                      <span className="text-teal-500">+</span>
                      Unlimited itineraries (vs 5 per quarter)
                    </li>
                    <li className="flex items-center gap-2 text-sm text-gray-700" style={FONTS.body}>
                      <span className="text-teal-500">+</span>
                      Unlimited destination recommendations (vs 10)
                    </li>
                    <li className="flex items-center gap-2 text-sm text-gray-700" style={FONTS.body}>
                      <span className="text-teal-500">+</span>
                      Unlimited modifications per itinerary (vs 15)
                    </li>
                    <li className="flex items-center gap-2 text-sm text-gray-700" style={FONTS.body}>
                      <span className="text-teal-500">+</span>
                      Forever trip history (free archives after 30 days)
                    </li>
                    <li className="flex items-center gap-2 text-sm text-gray-700" style={FONTS.body}>
                      <span className="text-teal-500">+</span>
                      Group planning + booking integration + points optimization
                    </li>
                    <li className="flex items-center gap-2 text-sm text-gray-700" style={FONTS.body}>
                      <span className="text-teal-500">+</span>
                      Save & sync itineraries across devices
                    </li>
                  </ul>
                  <button
                    onClick={async () => {
                      if (features.STRIPE_ENABLED) {
                        try {
                          const { getSession } = await import('../../lib/supabase');
                          const session = await getSession();
                          const headers = { 'Content-Type': 'application/json' };
                          if (session?.access_token) headers['Authorization'] = `Bearer ${session.access_token}`;
                          const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';
                          const res = await fetch(`${BACKEND_URL}/api/billing/create-checkout-session`, {
                            method: 'POST', headers, body: JSON.stringify({ interval: 'monthly' }),
                          });
                          const data = await res.json();
                          if (data.url) window.location.href = data.url;
                          else setMessage({ type: 'error', text: data.error || 'Could not start checkout.' });
                        } catch (err) {
                          setMessage({ type: 'error', text: 'Could not connect to billing service.' });
                        }
                      } else {
                        setMessage({ type: 'success', text: 'Thanks for your interest! Planner subscriptions are coming soon.' });
                      }
                    }}
                    className="w-full px-6 py-3 bg-gradient-to-r from-teal-500 to-teal-600 text-white rounded-xl hover:from-teal-600 hover:to-teal-700 transition-colors"
                    style={FONTS.bodyBold}
                  >
                    Upgrade to Planner - $12/month
                  </button>
                </div>
              )}
              {/* Manage Subscription for Planners */}
              {(profile?.tier === 'planner') && features.STRIPE_ENABLED && (
                <div className="bg-teal-50 rounded-2xl p-6 border border-teal-100">
                  <h4 className="text-gray-900 mb-2" style={FONTS.headingSemibold}>Planner Subscription</h4>
                  <p className="text-sm text-gray-600 mb-4" style={FONTS.body}>Manage your billing, update payment method, or cancel.</p>
                  <button
                    onClick={async () => {
                      try {
                        const session = await getSession();
                        const headers = { 'Content-Type': 'application/json' };
                        if (session?.access_token) headers['Authorization'] = `Bearer ${session.access_token}`;
                        const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';
                        const res = await fetch(`${BACKEND_URL}/api/billing/create-portal-session`, {
                          method: 'POST', headers,
                        });
                        const data = await res.json();
                        if (data.url) window.location.href = data.url;
                        else setMessage({ type: 'error', text: data.error || 'Could not open billing portal.' });
                      } catch (err) {
                        setMessage({ type: 'error', text: 'Could not connect to billing service.' });
                      }
                    }}
                    className="w-full px-6 py-3 bg-white text-gray-700 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors"
                    style={FONTS.bodyBold}
                  >
                    Manage Subscription
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Community Tab */}
          {activeTab === 'community' && (
            <div className="space-y-6">
              <React.Suspense fallback={<div className="text-center py-4 text-gray-400 text-xs">Loading profile editor...</div>}>
                <ProfileEditor profile={profile} onSave={async (data) => { await updateProfile(data); setMessage({ type: 'success', text: 'Profile saved' }); return data; }} onClose={() => {}} inline />
              </React.Suspense>
              {/* Log a Past Trip */}
              <div className="space-y-3">
                <h3 className="text-lg text-gray-900" style={FONTS.headingSemibold}>Log a Past Trip</h3>
                <p className="text-sm text-gray-500" style={FONTS.body}>Add trips you took outside Arrivo to build your travel profile.</p>
                {showSkeletonBuilder ? (
                  <React.Suspense fallback={<div className="text-center py-4 text-gray-400 text-xs">Loading...</div>}>
                    <SkeletonTripBuilder inline onClose={() => setShowSkeletonBuilder(false)} />
                  </React.Suspense>
                ) : showTripImporter ? (
                  <React.Suspense fallback={<div className="text-center py-4 text-gray-400 text-xs">Loading...</div>}>
                    <TripImporter inline onClose={() => setShowTripImporter(false)} />
                  </React.Suspense>
                ) : (
                  <div className="flex gap-3">
                    <button
                      onClick={() => setShowSkeletonBuilder(true)}
                      className="flex-1 py-3 rounded-xl text-sm font-semibold text-teal-600 border border-teal-200 hover:bg-teal-50 transition-colors flex items-center justify-center gap-2"
                      style={FONTS.bodySemibold}
                    >
                      <Plus className="w-4 h-4" /> Build Manually
                    </button>
                    <button
                      onClick={() => setShowTripImporter(true)}
                      className="flex-1 py-3 rounded-xl text-sm font-semibold text-white bg-teal-600 hover:bg-teal-700 transition-colors flex items-center justify-center gap-2"
                      style={FONTS.bodySemibold}
                    >
                      <FileText className="w-4 h-4" /> Import from Notes
                    </button>
                  </div>
                )}
              </div>

              {/* Privacy Settings */}
              <div className="space-y-3">
                <h3 className="text-lg text-gray-900" style={FONTS.headingSemibold}>Privacy Settings</h3>
                <PrivacySettings
                  settings={communitySettings}
                  onSave={handleSaveCommunitySettings}
                  onClose={() => {}}
                  inline
                />
              </div>
              <BlockedUsersList />
            </div>
          )}

          {/* Privacy Tab */}
          {activeTab === 'privacy' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg text-gray-900 mb-4" style={FONTS.headingSemibold}>Analytics & Tracking</h3>

                <div className="p-4 bg-gray-50 rounded-xl">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h4 className="text-gray-900" style={FONTS.bodyBold}>Anonymous Analytics</h4>
                      <p className="text-sm text-gray-600 mt-1" style={FONTS.body}>
                        Help us improve arrivo by sharing anonymous usage data.
                        We never sell your data or share it with third parties.
                      </p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer ml-4">
                      <input
                        type="checkbox"
                        checked={profile?.analytics_consent || false}
                        onChange={(e) => handleAnalyticsToggle(e.target.checked)}
                        disabled={isLoading}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-teal-200 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-teal-500"></div>
                    </label>
                  </div>
                  {profile?.analytics_consent_date && (
                    <p className="text-xs text-gray-500 mt-2" style={FONTS.body}>
                      Consented on {new Date(profile.analytics_consent_date).toLocaleDateString()}
                    </p>
                  )}
                </div>
              </div>

              <div>
                <h3 className="text-lg text-gray-900 mb-4" style={FONTS.headingSemibold}>What We Store</h3>
                <div className="space-y-3 text-sm">
                  <div className="flex items-start gap-3">
                    <span className="text-teal-500">&#10003;</span>
                    <div>
                      <span style={FONTS.bodyBold}>Profile & preferences</span>
                      <p className="text-gray-500" style={FONTS.body}>Name, email, travel style, budget, dietary needs, accessibility, safety preferences</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="text-teal-500">&#10003;</span>
                    <div>
                      <span style={FONTS.bodyBold}>Your trips</span>
                      <p className="text-gray-500" style={FONTS.body}>Saved itineraries, trip logs, place reviews</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="text-teal-500">&#10003;</span>
                    <div>
                      <span style={FONTS.bodyBold}>Community activity</span>
                      <p className="text-gray-500" style={FONTS.body}>Display name, bio, followers, reactions</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="text-teal-500">&#10003;</span>
                    <div>
                      <span style={FONTS.bodyBold}>Anonymous analytics</span>
                      <p className="text-gray-500" style={FONTS.body}>Feature usage (only if you opted in above)</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="text-red-500">&#10007;</span>
                    <div>
                      <span style={FONTS.bodyBold}>Never collected</span>
                      <p className="text-gray-500" style={FONTS.body}>Browsing history outside arrivo, payment details, data sold to third parties</p>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-lg text-gray-900 mb-4" style={FONTS.headingSemibold}>California Privacy Rights (CCPA)</h3>
                <div className="p-4 bg-gray-50 rounded-xl">
                  <p className="text-sm text-gray-600 mb-3" style={FONTS.body}>
                    arrivo does not sell or share your personal information with third parties for advertising purposes.
                    If you are a California resident and wish to exercise your rights under the CCPA, you may contact us.
                  </p>
                  <a
                    href="mailto:hello@arrivotravel.ai?subject=CCPA%20Do%20Not%20Sell%20or%20Share%20Request"
                    className="inline-flex items-center gap-2 text-sm text-teal-600 hover:text-teal-700 font-medium"
                    style={FONTS.bodyBold}
                  >
                    Do Not Sell or Share My Personal Information
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                  </a>
                </div>
              </div>
            </div>
          )}

          {/* Data Tab (GDPR) */}
          {activeTab === 'data' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg text-gray-900 mb-4" style={FONTS.headingSemibold}>Request Your Data</h3>
                <p className="text-sm text-gray-600 mb-4" style={FONTS.body}>
                  Download a copy of your data in JSON format. Your export will include:
                </p>
                <ul className="text-sm text-gray-500 mb-5 space-y-1.5" style={FONTS.body}>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-teal-400 flex-shrink-0" />
                    Profile info (display name, bio, travel preferences)
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-teal-400 flex-shrink-0" />
                    Saved itineraries &amp; trip logs
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-teal-400 flex-shrink-0" />
                    Reviews &amp; community activity
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-teal-400 flex-shrink-0" />
                    Follows, achievements &amp; kudos
                  </li>
                </ul>

                <div className="mb-4">
                  <label className="block text-sm text-gray-700 mb-1.5" style={FONTS.headingSemibold}>
                    Reason for request
                  </label>
                  <select
                    value={exportReason}
                    onChange={(e) => setExportReason(e.target.value)}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl bg-white text-sm text-gray-700 focus:ring-2 focus:ring-teal-300 focus:border-teal-500 outline-none"
                    style={FONTS.body}
                  >
                    <option value="">Select a reason (optional)</option>
                    <option value="personal_records">Personal records</option>
                    <option value="switching_service">Switching to another service</option>
                    <option value="privacy_review">Privacy review</option>
                    <option value="gdpr_request">GDPR / data rights request</option>
                    <option value="other">Other</option>
                  </select>
                  {exportReason === 'other' && (
                    <input
                      type="text"
                      value={exportReasonText}
                      onChange={(e) => setExportReasonText(e.target.value)}
                      placeholder="Please specify..."
                      className="w-full mt-2 px-4 py-2.5 border border-gray-200 rounded-xl text-sm text-gray-700 focus:ring-2 focus:ring-teal-300 focus:border-teal-500 outline-none"
                      style={FONTS.body}
                    />
                  )}
                </div>

                <button
                  onClick={handleExportData}
                  disabled={isLoading}
                  className="px-5 py-2.5 bg-[#313143] text-white rounded-xl hover:bg-[#3d3d52] transition-colors disabled:opacity-50"
                  style={FONTS.bodyBold}
                >
                  {isLoading ? 'Preparing...' : 'Request My Data'}
                </button>
              </div>

              <hr className="border-gray-100" />

              <div>
                <h3 className="text-lg text-red-600 mb-4" style={FONTS.headingSemibold}>Delete Account</h3>
                <p className="text-sm text-gray-600 mb-4" style={FONTS.body}>
                  Permanently delete your account and all associated data. This action
                  cannot be undone. Your data will be removed within 30 days per GDPR requirements.
                </p>

                {!showDeleteConfirm ? (
                  <button
                    onClick={() => setShowDeleteConfirm(true)}
                    className="px-5 py-2.5 border border-red-300 text-red-600 rounded-xl hover:bg-red-50 transition-colors"
                    style={FONTS.bodyBold}
                  >
                    Delete My Account
                  </button>
                ) : (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-xl">
                    <p className="text-sm text-red-800 mb-3" style={FONTS.body}>
                      Type <strong>DELETE</strong> to confirm permanent account deletion:
                    </p>
                    <input
                      type="text"
                      value={deleteConfirmText}
                      onChange={(e) => setDeleteConfirmText(e.target.value)}
                      placeholder="Type DELETE"
                      className="w-full px-4 py-2.5 border border-red-300 rounded-xl focus:ring-2 focus:ring-red-200 outline-none mb-3"
                      style={FONTS.body}
                    />
                    <div className="flex gap-2">
                      <button
                        onClick={handleDeleteAccount}
                        disabled={isLoading || deleteConfirmText !== 'DELETE'}
                        className="px-4 py-2.5 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-colors disabled:opacity-50"
                        style={FONTS.bodyBold}
                      >
                        {isLoading ? 'Processing...' : 'Confirm Delete'}
                      </button>
                      <button
                        onClick={() => {
                          setShowDeleteConfirm(false);
                          setDeleteConfirmText('');
                        }}
                        className="px-4 py-2.5 border border-gray-300 text-gray-600 rounded-xl hover:bg-gray-50 transition-colors"
                        style={FONTS.bodyBold}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>

              <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl">
                <h4 className="text-blue-900 mb-2" style={FONTS.bodyBold}>Your Rights (GDPR)</h4>
                <ul className="text-sm text-blue-800 space-y-1" style={FONTS.body}>
                  <li>&#8226; <strong>Access:</strong> Download all your data anytime</li>
                  <li>&#8226; <strong>Rectification:</strong> Update your profile to correct data</li>
                  <li>&#8226; <strong>Erasure:</strong> Delete your account and data</li>
                  <li>&#8226; <strong>Portability:</strong> Export data in machine-readable format</li>
                  <li>&#8226; <strong>Objection:</strong> Opt out of analytics at any time</li>
                </ul>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SettingsModal;
